import { z } from 'zod';
import { COMPONENT_CATALOG, type ComponentType, type GridSize } from './components';
import { PERMISSIONS, type Permission } from './permissions';
import { SETTINGS_CATALOG, type SettingsSectionId } from './settings';

/**
 * The module catalog: what a deployment can be *made of*.
 *
 * A client buys scheduling but not cost control, or infrastructure monitoring
 * without the automation engine. Until now the product had one answer to that
 * -- grant nobody the permissions -- which works and reads badly: the client's
 * own administrator, who holds everything by definition, would still see Costs
 * in the navigation of a deployment that has no cost feature.
 *
 * So enablement is a second, orthogonal gate. Permissions answer "may this
 * person?"; modules answer "does this deployment have it at all?". They are
 * composed in exactly one place -- `SessionService.resolve` intersects the
 * permissions a user's roles grant with the permissions their deployment's
 * modules govern -- and that single intersection is why nothing else in the
 * system needed a module check bolted on. The navigation, the settings
 * catalog, every `@RequirePermissions` guard and every `can()` in the frontend
 * were already permission-driven, so they all became module-aware for free.
 *
 * The alternative was a feature-flag lookup at each call site. That is the
 * same work, spread over sixty files, with sixty chances to forget one.
 *
 * ## The partition
 *
 * Every tenant permission belongs to exactly one module. `modules.test.ts`
 * asserts it -- total and disjoint -- because a permission in no module would
 * be silently withheld from everybody the moment filtering turned on, and a
 * permission in two would survive its module being switched off. The same
 * assertion covers component types and settings sections.
 */

export const MODULE_KEYS = [
  'core',
  'projects',
  'infrastructure',
  'integrations',
  'scheduling',
  'spatial',
  'costing',
  'register',
  'meetings',
  'documents',
  'collaboration',
  'automations',
  'fleet',
] as const;

export const moduleKeySchema = z.enum(MODULE_KEYS);
export type ModuleKey = (typeof MODULE_KEYS)[number];

/**
 * One tile a module wants on a deployment's first dashboard.
 *
 * A module declares what it would put in front of a reader who has never
 * configured anything, and `composeStarterDashboard` turns the declarations of
 * whichever modules are enabled into a laid-out view. That is why this is data
 * here rather than a hand-written document in the seed: the seed's dashboard is
 * a fixture for one demo project, and a real client's first dashboard has to
 * follow from what they actually bought.
 *
 * A type may appear more than once with different configuration -- three key
 * metrics are three tiles of one type -- so this is a list, not a set.
 */
export interface StarterComponent {
  componentType: ComponentType;
  /**
   * Overrides for the catalog's `defaultConfiguration`. Merged over it rather
   * than replacing it, so a module naming one field does not silently drop the
   * others when the catalog entry gains a new one.
   */
  configuration?: Record<string, unknown>;
  /** Overrides the catalog's `defaultSize`. Rare: the catalog's answer is the
   *  considered one, and a module wanting a different one should say why. */
  size?: GridSize;
}

export interface ModuleDefinition {
  key: ModuleKey;
  titleKey: string;
  descriptionKey: string;
  /**
   * False for `core` alone. A deployment without projects, users, roles or
   * dashboards is not a smaller product, it is an empty one -- and a provider
   * who could switch off `user.manage` would be one click from a deployment
   * nobody can administer.
   */
  optional: boolean;
  /** Tenant permissions withheld from everyone while this module is off. */
  permissions: readonly Permission[];
  /** Dashboard component types this module owns. */
  componentTypes: readonly ComponentType[];
  /**
   * What this module contributes to a deployment's first dashboard, in the
   * order it wants them. Every entry must name a type from `componentTypes`
   * above -- a module cannot place another module's tile, or the starter
   * dashboard would outlive the module that draws it. `modules.test.ts`
   * asserts it.
   *
   * An empty list is legal and means "this module has nothing a reader needs at
   * a glance", which is a real answer for a module whose value is entirely in
   * its own screen.
   */
  starterComponents: readonly StarterComponent[];
  /** Settings sections this module owns. */
  settingsSections: readonly SettingsSectionId[];
  /**
   * Modules this one cannot work without, because it names their resources
   * rather than merely sitting beside them. Automations trigger on
   * `integration.failed` and act with `integration.pause`, so an automations
   * module over a deployment with no integrations is a rule editor whose
   * every rule is unsatisfiable.
   */
  requires: readonly ModuleKey[];
  order: number;
}

export const MODULE_CATALOG = {
  core: {
    key: 'core',
    titleKey: 'platform.modules.core.title',
    descriptionKey: 'platform.modules.core.description',
    optional: false,
    permissions: [
      'view.manage_own',
      'view.manage_shared',
      'user.read',
      'user.manage',
      'role.manage',
      'audit.read',
      /* The counterparty register. In `core` rather than in `costing`, where
       * `cost.manage` used to govern it by implication: two modules name
       * parties now, and a register both of them need belongs to neither. */
      'party.read',
      'party.manage',
      /* Delegated administration of one's own organization. Core for the reason
       * the register is: who belongs to whom is not a feature, it is part of
       * what an account is (docs/organizations-design.md). */
      'party.members.read',
      'party.members.manage',
      'party.members.invite',
      /* Who may see whom across organizations -- part of what an account is,
       * for the same reason. */
      'directory.host',
      'directory.all',
    ],
    componentTypes: ['markdown-note'],
    /**
     * Nothing.
     *
     * `markdown-note` is deliberately not placed: its default configuration is
     * an empty body, so placing one would put a blank box on the first thing a
     * client ever sees. A note is worth having once somebody has something to
     * write in it -- and the tile that used to be here moved out with the
     * `projects` module below.
     */
    starterComponents: [],
    /* None. The organization register is core's, but it is a tab on the users
     * page rather than a settings section: every account belongs to one, so it
     * sits beside the people and the roles. */
    settingsSections: [],
    requires: [],
    order: 0,
  },
  /**
   * The infrastructure project, split out of `core` so that `core` means what
   * its own comment claims: users, roles, dashboards and the audit log, which
   * every organization has.
   *
   * A project is not one of those. An organization that exists to maintain its
   * own vehicle fleet and declare it to the authorities it serves
   * (docs/clean-vehicles-design.md section 5.8) has no undertaking with a
   * completion percentage, and before this split it got the Project tab, the
   * project settings and a readiness checklist demanding a project it would
   * never create.
   *
   * Deliberately *not* an `Organization.kind` column, which was the other
   * candidate. A kind would make "authority" and "operator" mutually exclusive,
   * and a municipal transport company that both runs buses and procures
   * services is both. Module selection already expresses that, and expresses it
   * per capability rather than per label.
   *
   * Everything project-scoped names this as a prerequisite, so switching it off
   * is refused while any of them is on -- which is `missingModuleRequirements`
   * doing the work it already did for automations over integrations.
   */
  projects: {
    key: 'projects',
    titleKey: 'platform.modules.projects.title',
    descriptionKey: 'platform.modules.projects.description',
    optional: true,
    permissions: ['project.read', 'project.create', 'project.update', 'project.delete'],
    componentTypes: ['project-summary'],
    starterComponents: [
      {
        componentType: 'project-summary',
        /* Dates included here where the catalog default omits them: on a first
           dashboard "when does this end" is a question the reader has, and a
           deployment that just passed the readiness check has a project. */
        configuration: { fields: ['status', 'health', 'progress', 'endsOn'] },
      },
    ],
    settingsSections: [],
    requires: [],
    order: 5,
  },
  infrastructure: {
    key: 'infrastructure',
    titleKey: 'platform.modules.infrastructure.title',
    descriptionKey: 'platform.modules.infrastructure.description',
    optional: true,
    permissions: ['infrastructure.read', 'infrastructure.manage'],
    componentTypes: ['kpi-metric', 'service-status', 'service-latency'],
    /**
     * Two counts and the list behind them.
     *
     * `service-latency` is left off deliberately: its configuration names
     * specific services by identifier, and a starter dashboard is composed
     * before anybody has said which services matter. A latency tile with an
     * empty series list renders its own "pick some services" empty state, which
     * is a worse first impression than not placing it.
     */
    starterComponents: [
      { componentType: 'kpi-metric', configuration: { metric: 'services.outage' } },
      { componentType: 'kpi-metric', configuration: { metric: 'services.degraded' } },
      { componentType: 'service-status', configuration: { limit: 8 } },
    ],
    settingsSections: ['infrastructure-types'],
    requires: [],
    order: 10,
  },
  integrations: {
    key: 'integrations',
    titleKey: 'platform.modules.integrations.title',
    descriptionKey: 'platform.modules.integrations.description',
    optional: true,
    permissions: ['integration.read', 'integration.configure', 'integration.retry'],
    componentTypes: ['integration-health'],
    starterComponents: [{ componentType: 'integration-health' }],
    settingsSections: [],
    requires: [],
    order: 20,
  },
  scheduling: {
    key: 'scheduling',
    titleKey: 'platform.modules.scheduling.title',
    descriptionKey: 'platform.modules.scheduling.description',
    optional: true,
    permissions: [
      'schedule.read',
      'schedule.manage',
      'schedule.progress.report',
      'schedule.calendar.manage',
      'schedule.baseline.manage',
    ],
    componentTypes: ['schedule-overview', 'schedule-gantt', 'schedule-board'],
    starterComponents: [{ componentType: 'schedule-overview' }],
    settingsSections: ['schedule-calendar'],
    requires: ['projects'],
    order: 40,
  },
  /**
   * The map.
   *
   * It requires nothing, which is worth stating because it looks as though it
   * should require both scheduling and infrastructure: a work area is usually a
   * schedule task and an asset is usually a service. The link is optional in
   * both directions -- a feature with no subject is an ordinary feature, and the
   * read path resolves subjects it can and leaves the rest as plain geometry --
   * so a deployment with only the map still gets a working map, and switching
   * scheduling off leaves the work areas drawn rather than the module broken.
   *
   * That is the opposite of the automations case above, where a rule that
   * cannot name an integration has nothing left to say.
   */
  spatial: {
    key: 'spatial',
    titleKey: 'platform.modules.spatial.title',
    descriptionKey: 'platform.modules.spatial.description',
    optional: true,
    permissions: ['map.read', 'map.manage'],
    componentTypes: ['project-map'],
    /* The map is the largest tile in the catalog and it earns the space on a
       first dashboard: most of what this product tracks happens somewhere, and
       the map is the only tile that says where. */
    starterComponents: [{ componentType: 'project-map' }],
    settingsSections: ['map-basemap', 'map-defaults', 'map-reference-lines'],
    requires: ['projects'],
    order: 45,
  },
  costing: {
    key: 'costing',
    titleKey: 'platform.modules.costing.title',
    descriptionKey: 'platform.modules.costing.description',
    optional: true,
    permissions: [
      'cost.read',
      'cost.read_detail',
      'cost.manage',
      'cost.budget.approve',
      'cost.source.manage',
    ],
    componentTypes: ['cost-summary', 'cost-curve'],
    /**
     * The figures and the curve, in that order.
     *
     * Costing is the module whose absence from the dashboard was most obviously
     * wrong: it is the one a client's own management asks about, and until now
     * the only way to see a budget was to navigate to it. Both tiles are placed
     * because they answer different questions -- "what are the numbers" and
     * "where is this heading" -- and a first dashboard that answered only the
     * first would still send the reader to the Costs page for the second.
     */
    starterComponents: [{ componentType: 'cost-summary' }, { componentType: 'cost-curve' }],
    settingsSections: [],
    requires: ['projects'],
    order: 50,
  },
  /**
   * The project register: risks, opportunities, ideas, decisions, issues.
   *
   * It requires nothing, including meetings. A risk register is a standard
   * project artefact that stands on its own, and a decision recorded by email
   * is still a decision -- which is also why decisions live here rather than as
   * children of the meeting they were taken in.
   */
  register: {
    key: 'register',
    titleKey: 'platform.modules.register.title',
    descriptionKey: 'platform.modules.register.description',
    optional: true,
    permissions: ['register.read', 'register.manage'],
    componentTypes: ['register-summary'],
    starterComponents: [{ componentType: 'register-summary' }],
    settingsSections: [],
    requires: ['projects'],
    order: 52,
  },
  /**
   * Meetings, agendas and minutes.
   *
   * Requires neither scheduling nor the register, for the reason the spatial
   * module's comment above gives for itself: an agenda item referencing nothing
   * but its own text is a working agenda item, and the reference list resolves
   * what it can and drops what it cannot. Switching scheduling off leaves the
   * minutes readable rather than the module broken.
   *
   * It is much better with both, which is a different statement from needing
   * them.
   */
  documents: {
    key: 'documents',
    titleKey: 'platform.modules.documents.title',
    descriptionKey: 'platform.modules.documents.description',
    optional: true,
    permissions: ['document.read', 'document.manage'],
    componentTypes: ['document-register'],
    /*
     * One tile: the documents nothing has been issued for yet.
     *
     * A count of documents is furniture -- a project has as many as it has.
     * What a reader acts on is the drawing somebody is waiting for, which is
     * exactly the row with no issued revision.
     */
    starterComponents: [{ componentType: 'document-register' }],
    settingsSections: [],
    requires: ['projects'],
    order: 55,
  },
  meetings: {
    key: 'meetings',
    titleKey: 'platform.modules.meetings.title',
    descriptionKey: 'platform.modules.meetings.description',
    optional: true,
    permissions: ['meeting.read', 'meeting.manage', 'meeting.approve'],
    componentTypes: ['upcoming-meetings'],
    starterComponents: [{ componentType: 'upcoming-meetings' }],
    settingsSections: [],
    requires: ['projects'],
    order: 55,
  },
  /**
   * Comments and votes on anything the platform holds
   * (docs/register-collaboration-design.md section 6).
   *
   * Its own key rather than a corner of `core`, because "this deployment does
   * not want a discussion thread in the tool" is a real decision a provider
   * makes for a client -- some clients minute their disagreements and want them
   * minuted, not commented -- and switching it off should take the panel off
   * every screen through the one intersection that already does this, rather
   * than through a flag each screen has to remember to read.
   *
   * It requires nothing. A comment thread hangs off whatever kinds happen to be
   * enabled: the catalog says which kinds take comments, `readableKindsFor`
   * says which of those the caller may see, and a deployment with only
   * infrastructure switched on simply has nothing to comment on -- which is a
   * quiet feature rather than a broken module.
   */
  collaboration: {
    key: 'collaboration',
    titleKey: 'platform.modules.collaboration.title',
    descriptionKey: 'platform.modules.collaboration.description',
    optional: true,
    permissions: ['discussion.read', 'discussion.write', 'discussion.moderate'],
    componentTypes: ['discussion-activity'],
    starterComponents: [{ componentType: 'discussion-activity' }],
    settingsSections: [],
    requires: [],
    order: 58,
  },
  automations: {
    key: 'automations',
    titleKey: 'platform.modules.automations.title',
    descriptionKey: 'platform.modules.automations.description',
    optional: true,
    permissions: ['automation.read', 'automation.execute', 'automation.manage'],
    componentTypes: ['automation-activity'],
    starterComponents: [{ componentType: 'automation-activity' }],
    settingsSections: [],
    requires: ['integrations'],
    order: 60,
  },
  /**
   * Clean-vehicle fleet compliance (docs/clean-vehicles-design.md).
   *
   * `requires: []`, which is the whole point of the `party.*` move above and of
   * splitting `projects` out of `core`: an authority can buy fleet compliance
   * without cost control, and an organization that only maintains its own fleet
   * runs on `['core', 'fleet']` and nothing else.
   *
   * Organization-scoped throughout. A framework agreement for waste collection
   * is not part of an infrastructure project, and nothing in this module is
   * addressed by project id.
   */
  fleet: {
    key: 'fleet',
    titleKey: 'platform.modules.fleet.title',
    descriptionKey: 'platform.modules.fleet.description',
    optional: true,
    permissions: [
      'fleet.read',
      'fleet.manage',
      'fleet.disclose',
      'fleet.agreement.read',
      'fleet.agreement.manage',
      'fleet.verify',
      'fleet.report.read',
    ],
    componentTypes: [
      'fleet-compliance',
      'fleet-data-quality',
      'fleet-agreement-expiry',
      'fleet-energy-mix',
      'fleet-verification',
    ],
    /* The attainment tile alone. It is the number the module was bought for,
       and the other two answer questions a reader only has once they have seen
       it. */
    starterComponents: [{ componentType: 'fleet-compliance' }],
    settingsSections: ['fleet'],
    requires: [],
    order: 62,
  },
} as const satisfies Record<ModuleKey, ModuleDefinition>;

export const MODULES: readonly ModuleDefinition[] = Object.values(MODULE_CATALOG).sort(
  (a, b) => a.order - b.order,
);

/** Switched on for a deployment nobody has configured yet: everything. */
export const DEFAULT_ENABLED_MODULES: readonly ModuleKey[] = MODULE_KEYS;

/** Modules that cannot be switched off, whatever a request asks for. */
export const REQUIRED_MODULES: readonly ModuleKey[] = MODULES.filter(
  (module) => !module.optional,
).map((module) => module.key);

export function getModuleDefinition(key: string): ModuleDefinition | undefined {
  if (!Object.hasOwn(MODULE_CATALOG, key)) return undefined;
  return (MODULE_CATALOG as Record<string, ModuleDefinition>)[key];
}

export function isModuleKey(value: string): value is ModuleKey {
  return Object.hasOwn(MODULE_CATALOG, value);
}

/**
 * Normalizes a stored or requested selection: unknown keys dropped, mandatory
 * modules forced on, order made canonical.
 *
 * Unknown keys are dropped rather than rejected because the selection outlives
 * the code that wrote it. A deployment configured while a module existed, then
 * upgraded past its removal, must still boot -- a hard failure here would take
 * the whole tenant down over a capability nobody uses any more.
 */
export function normalizeModuleSelection(requested: readonly string[]): ModuleKey[] {
  const chosen = new Set<ModuleKey>(REQUIRED_MODULES);
  for (const key of requested) {
    if (isModuleKey(key)) chosen.add(key);
  }
  return MODULE_KEYS.filter((key) => chosen.has(key));
}

/**
 * Modules that are selected but whose prerequisites are not.
 *
 * Returned as pairs rather than thrown, so the provider console can name both
 * halves of the problem: "Automations needs Integrations" is actionable,
 * "invalid selection" is not.
 */
export function missingModuleRequirements(
  selection: readonly ModuleKey[],
): Array<{ module: ModuleKey; requires: ModuleKey }> {
  const enabled = new Set(selection);
  const gaps: Array<{ module: ModuleKey; requires: ModuleKey }> = [];
  for (const key of selection) {
    for (const required of MODULE_CATALOG[key].requires) {
      if (!enabled.has(required)) gaps.push({ module: key, requires: required });
    }
  }
  return gaps;
}

/**
 * The tenant permissions a selection of modules governs.
 *
 * This is the set a user's granted permissions are intersected with. Note what
 * it is not: it is not a grant. A viewer in a deployment with every module on
 * still holds only what their roles gave them.
 */
export function permissionsForModules(selection: readonly ModuleKey[]): Set<Permission> {
  const allowed = new Set<Permission>();
  for (const key of selection) {
    const definition = getModuleDefinition(key);
    if (!definition) continue;
    for (const permission of definition.permissions) allowed.add(permission);
  }
  return allowed;
}

/** Component types the given modules bring with them. */
export function componentTypesForModules(selection: readonly ModuleKey[]): Set<ComponentType> {
  const types = new Set<ComponentType>();
  for (const key of selection) {
    const definition = getModuleDefinition(key);
    if (!definition) continue;
    for (const type of definition.componentTypes) types.add(type);
  }
  return types;
}

/** The module a component type belongs to, or undefined if the catalogs drifted. */
export function moduleForComponentType(type: string): ModuleDefinition | undefined {
  return MODULES.find((module) => (module.componentTypes as readonly string[]).includes(type));
}

/** All component types, whatever module they belong to. Used by the tests and the console. */
export const ALL_COMPONENT_TYPES = Object.keys(COMPONENT_CATALOG) as ComponentType[];

/** All settings section ids. Used by the partition test. */
export const ALL_SETTINGS_SECTION_IDS = Object.keys(SETTINGS_CATALOG) as SettingsSectionId[];

/** All tenant permissions. Re-exported so the partition test reads in one place. */
export const ALL_TENANT_PERMISSIONS: readonly Permission[] = PERMISSIONS;

/**
 * The component selection a deployment starts with, and the ceiling any later
 * selection is clamped to: a component whose module is off is not offered,
 * whatever the stored list says.
 *
 * Keeping the two rules in one function is what stops them disagreeing. A
 * provider switches Scheduling off; the schedule overview tile disappears from
 * the picker without anybody editing the component list, and reappears exactly
 * as it was if Scheduling comes back -- because the stored list was never
 * rewritten, only filtered.
 */
export function effectiveComponentSelection(
  modules: readonly ModuleKey[],
  requestedComponents: readonly string[],
): ComponentType[] {
  const fromModules = componentTypesForModules(modules);
  const requested = new Set(requestedComponents);
  return ALL_COMPONENT_TYPES.filter((type) => fromModules.has(type) && requested.has(type));
}
